import type { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { createMsal } from '@reinz/auth/clientConfig'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { type AppConfig, AppConfigProvider } from '@/Config/AppConfigContext'
import { fetchConfig } from '@/Config/ConfigService'
import { AppUpdatePrompt } from '@/components/AppUpdatePrompt'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { installFetchInterceptor } from '@/lib/fetchInterceptor'
import { getActiveSessionId } from '@/lib/sessionTracker'
import { clearAppLocalStorage } from '@/lib/storage'
import { telemetry } from '@/lib/telemetry'
import Index from './pages/Index'
import MagicLinkVerify from './pages/MagicLinkVerify'
import NotFound from './pages/NotFound'

const queryClient = new QueryClient()

export default function App() {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null)
  const [pca, setPca] = useState<IPublicClientApplication | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let t: number | null = null
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'visibilitychange']
    let handler: () => void = () => {}

    ;(async () => {
      try {
        const cfg = (await fetchConfig()) as AppConfig
        if (!cfg) throw new Error('Config unavailable')
        setAppConfig(cfg)

        const msalInstance = createMsal(cfg, 'localStorage')
        await msalInstance.initialize()

        setPca(msalInstance)
        installFetchInterceptor(msalInstance, cfg.apiScopes ?? [])

        const IDLE_LIMIT_MS = 8 * 60 * 60 * 1000
        let hiddenAt: number | null = null

        const doLogout = () => {
          const sessionId = getActiveSessionId()
          if (sessionId) {
            fetch(`/api/user-sessions/${sessionId}/logout`, {
              method: 'POST',
              keepalive: true,
            }).catch(() => {})
          }
          // MSAL 5 only takes onRedirectNavigate in its instance config, so storage is cleared
          // before the redirect instead. That wipes MSAL's cache too, so the account and its
          // ID token are read first and passed in for B2C to end the right session.
          const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
          sessionStorage.clear()
          clearAppLocalStorage()
          msalInstance.logoutRedirect({ account, idTokenHint: account?.idToken })
        }

        const reset = () => {
          if (t) clearTimeout(t)
          t = window.setTimeout(doLogout, IDLE_LIMIT_MS)
        }

        handler = () => {
          if (document.visibilityState === 'hidden') {
            // record when the tab went to background
            hiddenAt = Date.now()
          } else if (document.visibilityState === 'visible') {
            // check if user was away long enough to log out
            if (hiddenAt !== null && Date.now() - hiddenAt >= IDLE_LIMIT_MS) {
              doLogout()
            } else {
              reset()
            }
            hiddenAt = null
          } else {
            reset()
          }
        }

        events.forEach((ev) => document.addEventListener(ev, handler, { passive: true }))
        reset() // start the timer
      } catch (e: any) {
        telemetry.captureError(e)
        setError(e?.message ?? 'Failed to initialise app')
      }
    })()

    // Cleanup runs on unmount
    return () => {
      if (t) clearTimeout(t)
      events.forEach((ev) => document.removeEventListener(ev, handler))
    }
  }, [])

  if (error) {
    return <div className="p-6 text-center text-red-600">{error}</div>
  }

  if (!appConfig || !pca) {
    return <div className="p-6 text-center">Loading configuration…</div>
  }

  return (
    <AppConfigProvider config={appConfig}>
      <MsalProvider instance={pca}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <AppUpdatePrompt />
              <Routes>
                <Route path="/:tab?" element={<Index />} />
                <Route path="/magic-link" element={<MagicLinkVerify />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </MsalProvider>
    </AppConfigProvider>
  )
}
